import React, { createContext, useState, useEffect, useContext } from 'react';

export const NetworkContext = createContext({
  isOnline: true,
  lastOnline: null,
});

export const NetworkProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastOnline, setLastOnline] = useState(
    isOnline ? new Date() : null
  );

  useEffect(() => {
    // Handle online status changes
    const handleOnline = () => {
      console.log('✅ App is online');
      setIsOnline(true);
      setLastOnline(new Date());
    };

    // Handle offline status changes
    const handleOffline = () => {
      console.log('❌ App is offline');
      setIsOnline(false);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Also periodically check connectivity by pinging a reliable endpoint
    const checkConnection = async () => {
      try {
        // Try to fetch a small file from Firebase CDN - should be fast and reliable
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch(
          'https://www.gstatic.com/firebasejs/ping',
          { 
            method: 'HEAD',
            cache: 'no-store',
            signal: controller.signal 
          }
        );
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          if (!isOnline) {
            console.log('✅ Connection restored (ping successful)');
            setIsOnline(true);
            setLastOnline(new Date());
          }
        } else {
          // Only set offline if the browser also reports offline
          if (isOnline && !navigator.onLine) {
            console.log('❌ Connection lost (ping failed with status)', response.status);
            setIsOnline(false);
          }
        }
      } catch (error) {
        // Only set offline if both the ping fails AND the browser reports offline
        if (error.name !== 'AbortError' && isOnline && !navigator.onLine) {
          console.log('❌ Connection lost (ping error):', error);
          setIsOnline(false);
        }
      }
    };

    // More robust connection checker - only mark as offline if multiple checks fail
    let failedChecks = 0;
    const MAX_FAILED_CHECKS = 2;
    
    const robustCheckConnection = async () => {
      try {
        await checkConnection();
        // Reset failed checks counter on success
        failedChecks = 0;
      } catch (error) {
        // Increment failed checks
        failedChecks++;
        console.log(`Connection check failed ${failedChecks}/${MAX_FAILED_CHECKS} times`);
        
        // Only set offline status after multiple consecutive failures
        if (failedChecks >= MAX_FAILED_CHECKS && navigator.onLine === false) {
          setIsOnline(false);
        }
      }
    };
    
    // Check connection every 30 seconds
    const interval = setInterval(robustCheckConnection, 30000);
    
    // Initial check - just check if browser reports online
    setIsOnline(navigator.onLine);

    // Clean up on unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [isOnline]);

  return (
    <NetworkContext.Provider value={{ isOnline, lastOnline }}>
      {children}
    </NetworkContext.Provider>
  );
};

// Custom hook for easier access to the context
export const useNetwork = () => useContext(NetworkContext);
